/**
 * SCH-5.9 — Insights: four sync-health trends on one screen.
 *
 * The Sync History tab answers "what happened in that run". This one answers "is it getting
 * worse" — a creeping failure rate, a duration climbing all week, intermittent auth errors —
 * which a paginated list cannot show. It is a health glance, not analytics: four charts, one
 * panel, three ranges.
 *
 * ⚠️ A BUCKET WITH NO RUNS HAS NO SUCCESS RATE, AND THAT IS WHY THE LINES BREAK.
 * The whole stack keeps null and zero apart deliberately — Postgres returns NULL, the endpoint
 * keeps Optional, the service maps `?? null`, `toChartPoints` passes it through. It lands here as
 * `connectNulls={false}`, so a quiet day is a gap rather than a spike to the floor. Setting
 * connectNulls, or coercing the null anywhere upstream, turns "nothing was scheduled" into
 * "everything failed" — this tab's alarm, raised falsely, which is worse than not raising it.
 *
 * All the logic lives in ./insightsMetrics, tested in insightsMetrics.test.ts. That is not
 * fastidiousness: vitest here collects only `src/**\/*.test.ts` in a `node` environment with no
 * jsdom installed, so a test file beside this component would never run. Three in this directory
 * already don't.
 *
 * Colors are literal palette values rather than theme tokens, matching the emerald/amber/blue
 * vocabulary SyncHistoryTab already uses for SCH status.
 */

import { useMemo, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Button } from '@/components/ui/button';
import { useSyncMetrics } from '@/hooks/integrations/useSyncMetrics';
import {
  SYNC_METRICS_RANGES,
  type SyncMetricsRange,
} from '@/types/integrations/syncMetrics';

import {
  authFailureWarning,
  bucketForRange,
  formatDuration,
  formatPercent,
  insightsViewState,
  RANGE_LABELS,
  summarize,
  toChartPoints,
} from './insightsMetrics';

interface ChartCardProps {
  title: string;
  stat: string;
  hint: string;
  tone?: 'default' | 'alert';
  children: React.ReactNode;
}

/**
 * recharts types a tooltip value as `ValueType` (number | string | array), so the null we went to
 * such lengths to preserve arrives untyped. Narrow it back rather than casting: anything that is
 * not a number — including the null of an empty bucket — has no rate to show.
 */
function _asRate(value: unknown): number | null {
  return typeof value === 'number' ? value / 100 : null;
}

function ChartCard({ title, stat, hint, tone = 'default', children }: ChartCardProps) {
  return (
    <div className="rounded-md border bg-card p-4">
      <div className="mb-3 flex items-baseline justify-between gap-2">
        <div>
          <h4 className="text-sm font-medium">{title}</h4>
          <p className="text-xs text-muted-foreground">{hint}</p>
        </div>
        <span
          className={
            tone === 'alert'
              ? 'text-lg font-semibold text-rose-600'
              : 'text-lg font-semibold text-foreground'
          }
        >
          {stat}
        </span>
      </div>
      <div className="h-40 w-full">
        <ResponsiveContainer width="100%" height="100%">
          {children as React.ReactElement}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function InsightsTab() {
  const [range, setRange] = useState<SyncMetricsRange>('30d');
  const bucket = bucketForRange(range);

  const { data: result, isLoading, error } = useSyncMetrics(range, bucket);

  const state = useMemo(
    () => insightsViewState({ isLoading, queryError: error, result }),
    [isLoading, error, result],
  );
  const points = state.kind === 'ready' ? state.points : [];
  const chartData = useMemo(() => toChartPoints(points, bucket), [points, bucket]);
  const headline = useMemo(() => summarize(points), [points]);
  const authWarning = authFailureWarning(headline, range);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-sm font-medium">Sync health trends</h3>
          <p className="text-xs text-muted-foreground">
            Spot a rising failure rate or a creeping duration before it trips the auto-pause
            threshold.
          </p>
        </div>
        <div className="flex items-center gap-1" role="group" aria-label="Range">
          {SYNC_METRICS_RANGES.map((option) => (
            <Button
              key={option}
              size="sm"
              variant={option === range ? 'default' : 'outline'}
              onClick={() => setRange(option)}
              aria-pressed={option === range}
            >
              {RANGE_LABELS[option]}
            </Button>
          ))}
        </div>
      </div>

      {state.kind === 'loading' && (
        <div className="flex items-center gap-2 rounded-md border bg-card p-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading sync insights…
        </div>
      )}

      {state.kind === 'error' && (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          {state.message}
        </div>
      )}

      {state.kind === 'empty' && (
        <div className="rounded-md border bg-card p-6 text-center text-sm text-muted-foreground">
          No sync activity in this range.
        </div>
      )}

      {state.kind === 'ready' && (
        <>
          {authWarning && (
            <div className="flex items-start gap-2 rounded-md border border-rose-300 bg-rose-50 p-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />
              <p className="text-sm text-rose-900">{authWarning}</p>
            </div>
          )}

          <div className="grid gap-4 lg:grid-cols-2">
            <ChartCard
              title="Tick success rate"
              hint="Successful runs ÷ runs. Gaps are periods with no runs at all."
              stat={formatPercent(headline.successRate)}
            >
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} minTickGap={20} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" width={44} />
                <RechartsTooltip formatter={(value) => formatPercent(_asRate(value))} />
                <Line
                  type="monotone"
                  dataKey="successPercent"
                  name="Success rate"
                  stroke="#059669"
                  strokeWidth={2}
                  dot={false}
                  connectNulls={false}
                />
              </LineChart>
            </ChartCard>

            <ChartCard
              title="Average run duration"
              hint="Mean wall-clock per run. A steady climb usually means catalog growth."
              stat={formatDuration(headline.avgDurationMs)}
            >
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} minTickGap={20} />
                <YAxis tick={{ fontSize: 11 }} unit="s" width={44} />
                <RechartsTooltip
                  formatter={(value) =>
                    typeof value === 'number' ? `${value}s` : '—'
                  }
                />
                <Line
                  type="monotone"
                  dataKey="durationSeconds"
                  name="Duration"
                  stroke="#2563eb"
                  strokeWidth={2}
                  dot={false}
                  connectNulls={false}
                />
              </LineChart>
            </ChartCard>

            <ChartCard
              title="Projects changed"
              hint="Projects actually written per bucket. Zero here is real, not a gap."
              stat={String(headline.projectsChanged)}
            >
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} minTickGap={20} />
                <YAxis tick={{ fontSize: 11 }} width={44} allowDecimals={false} />
                <RechartsTooltip />
                <Bar dataKey="projectsChanged" name="Projects changed" fill="#7c3aed" />
              </BarChart>
            </ChartCard>

            <ChartCard
              title="Authentication failures"
              hint="Runs paused with reason auth_failed. Any bar here needs a key check."
              stat={String(headline.authFailures)}
              tone={headline.authFailures > 0 ? 'alert' : 'default'}
            >
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} minTickGap={20} />
                <YAxis tick={{ fontSize: 11 }} width={44} allowDecimals={false} />
                <RechartsTooltip />
                <Bar dataKey="authFailures" name="Auth failures" fill="#e11d48" />
              </BarChart>
            </ChartCard>
          </div>

          <p className="text-xs text-muted-foreground">
            {headline.totalRuns} {headline.totalRuns === 1 ? 'run' : 'runs'} in the last{' '}
            {RANGE_LABELS[range]}, bucketed by {bucket}.
          </p>
        </>
      )}
    </div>
  );
}
